import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, ArrowRight, Heart, Shirt, Music, BookOpen, Baby } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSettings, DIRECTIONS_URL } from "@/hooks/SettingsContext";
import { trackService1Submit, trackPlanVisitClick, trackDirectionsClick } from "@/lib/analytics";
import SEO from "@/components/SEO";
import LocationMarquee from "@/components/LocationMarquee";
import { Reveal, LineReveal, Chapter, Stagger, StaggerItem } from "@/components/Reveal";
import InviteShare from "@/components/InviteShare";
import { Field, TextInput, SubmitButton, inputCls, HEAR_OPTIONS } from "@/components/forms";

const FAMILY_IMG = "/family-embrace.jpg";

const churchJsonLd = {
  "@context": "https://schema.org",
  "@type": "Church",
  name: "Victory Chapel Clay",
  alternateName: ["Victory Chapel Christian Church", "VC Clay"],
  description: "A new Christian church near CR 220 & Henley Road in the Lake Asbury area of Clay County, Florida - a family church serving Lake Asbury, Middleburg, Oakleaf, Fleming Island, Orange Park and surrounding communities. Launching January 2027.",
  url: "https://vcclaychurch.com/",
  address: {
    "@type": "PostalAddress",
    streetAddress: "3180 CR 220 STE 6-7",
    addressLocality: "Middleburg",
    addressRegion: "FL",
    postalCode: "32068",
    addressCountry: "US",
  },
  areaServed: ["Lake Asbury", "Middleburg", "Oakleaf", "Fleming Island", "Orange Park", "Green Cove Springs", "Lakeside", "Keystone Heights", "Doctors Inlet", "Clay County"],
  email: "Victorychapelclay@gmail.com",
  telephone: "+1-904-383-2559",
};

function Service1Form() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", hear_about: "", connect_ok: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/register/service-1", form);
      trackService1Submit();
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl bg-navy p-10 text-center text-white shadow-lift" data-testid="service1-success">
        <h3 className="font-display text-3xl font-800 tracking-tight">WE CAN'T WAIT TO MEET YOU!</h3>
        <p className="mt-4 text-white/75">We'll send you the details for Service #1 and a reminder as launch day gets closer.</p>
        <InviteShare />
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-lift sm:p-10" data-testid="service1-form">
      <h3 className="font-display text-2xl font-800 tracking-tight text-navy" data-testid="service1-form-heading">PLAN YOUR VISIT</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        Planning to join us for Service #1? Let us know you're coming - 30 seconds, and we'll be ready for you.
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field label="First Name" id="s1-first" required>
          <TextInput id="s1-first" required value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
        </Field>
        <Field label="Last Name" id="s1-last" required>
          <TextInput id="s1-last" required value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
        </Field>
        <Field label="Email" id="s1-email" required>
          <TextInput id="s1-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
        </Field>
        <Field label="Phone" id="s1-phone" hint="Optional">
          <TextInput id="s1-phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Where did you hear about us?" id="s1-hear" hint="Optional">
            <select id="s1-hear" data-testid="s1-hear" value={form.hear_about} onChange={set("hear_about")} className={inputCls}>
              <option value="">Select one...</option>
              {HEAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        </div>
        <label htmlFor="s1-connect" className="flex cursor-pointer items-start gap-3 text-sm text-slate-600 sm:col-span-2">
          <input id="s1-connect" type="checkbox" data-testid="s1-connect" checked={form.connect_ok}
                 onChange={(e) => setForm((f) => ({ ...f, connect_ok: e.target.checked }))}
                 className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C8102E]" />
          Yes, it's okay to connect with us ahead of time.
        </label>
      </div>
      <div className="mt-7">
        <SubmitButton testId="service1-submit" loading={loading}>PLAN MY VISIT</SubmitButton>
        <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="service1-form-note">
          We'll reach out with everything you need - and on launch day, we'll be watching for you at the door.
        </p>
      </div>
    </form>
  );
}

function PreLaunchHero({ settings }) {
  return (
    <section className="relative flex min-h-[82svh] items-end overflow-hidden bg-navy text-white sm:min-h-[88vh]" data-testid="prelaunch-hero">
      <img
        src="/home-worship.jpg"
        alt="A church family worshipping together with raised hands - there's a place for you at Victory Chapel Clay"
        width="1254"
        height="1254"
        loading="eager"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
        data-testid="hero-belong-photo"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/25" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-transparent" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-40 sm:px-8 sm:pb-20">
        <LineReveal
          className="max-w-3xl font-display text-5xl font-black leading-[1.02] tracking-tighter sm:text-7xl lg:text-8xl"
          lines={["THERE'S A PLACE", "FOR YOU HERE."]}
          delay={0.15}
        />
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
          className="mt-4 font-serif text-xl italic text-white/85 sm:text-2xl">
          A new church in the heart of Clay County.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <a href="#service-1" data-testid="hero-service1-cta"
             className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-9 py-4 font-display text-base font-700 tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark hover:shadow-lift">
            PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
          </a>
          <Link to="/im-new" data-testid="hero-what-to-expect-cta"
             className="font-display text-sm font-700 tracking-wide text-white/70 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white">
            What to expect
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-9 border-t border-white/15 pt-5" data-testid="hero-service-info">
          <p className="font-display text-lg font-800 tracking-[0.14em] text-white sm:text-xl">
            LAUNCHING <span className="text-rose-300">JANUARY 2027</span>
          </p>
          <p className="mt-1.5 text-sm text-white/70 sm:text-base">
            Sundays {settings?.sunday_time || "11:00 AM"} <span className="mx-1.5 text-white/35" aria-hidden="true">&middot;</span> Wednesdays {settings?.wednesday_time || "7:00 PM"}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function PostLaunchHero({ settings }) {
  const hasTimes = settings?.sunday_time || settings?.wednesday_time;
  return (
    <section className="relative overflow-hidden bg-navy text-white" data-testid="postlaunch-hero">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={FAMILY_IMG} alt="" className="h-full w-full object-cover opacity-35" loading="eager" />
        <div className="absolute inset-0 bg-navy/60" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 py-20 text-center sm:px-8 sm:py-28">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <img
            src="/vc-clay-logo-512.png"
            alt="VC Clay - the official Victory Chapel logo"
            width="128"
            height="128"
            className="mx-auto h-24 w-24 rounded-full bg-white p-1 shadow-lift ring-1 ring-white/25 sm:h-32 sm:w-32"
            data-testid="hero-logo"
          />
        </motion.div>
        <Reveal delay={0.15}>
          <p className="mt-5 font-display text-lg font-800 tracking-[0.14em] text-white sm:text-2xl">VICTORY CHAPEL</p>
          <p className="mt-1 font-serif text-sm italic text-rose-300 sm:text-base">Clay County, Florida</p>
        </Reveal>
        <div className="mt-8">
          <LineReveal
            className="text-center font-display text-5xl font-900 leading-[0.95] tracking-tighter sm:text-7xl"
            lines={["YOU BELONG", "HERE."]}
          />
        </div>
        <Reveal delay={0.3}>
          <p className="mt-4 font-serif text-xl italic text-white/85 sm:text-2xl">A church for Clay County.</p>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            Victory Chapel is a place where you can encounter Jesus, build meaningful relationships,
            strengthen your family, and discover God's purpose for your life.
          </p>
        </Reveal>
        <Reveal delay={0.45}>
          {hasTimes && (
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
              {settings.sunday_time && (
                <span className="rounded-full border border-white/25 bg-white/10 px-5 py-2.5 font-semibold backdrop-blur-sm" data-testid="hero-sunday-time">
                  SUNDAY SERVICE - {settings.sunday_time}
                </span>
              )}
              {settings.wednesday_time && (
                <span className="rounded-full border border-white/25 bg-white/10 px-5 py-2.5 font-semibold backdrop-blur-sm" data-testid="hero-wednesday-time">
                  WEDNESDAY SERVICE - {settings.wednesday_time}
                </span>
              )}
            </div>
          )}
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="hero-plan-visit-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-8 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
              PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={trackDirectionsClick} data-testid="hero-directions-cta"
               className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-base font-700 text-white transition-colors duration-300 hover:bg-white/10">
              GET DIRECTIONS <MapPin size={18} aria-hidden="true" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhatToExpect() {
  const items = [
    { icon: Shirt, title: "Come As You Are", text: "Jeans, t-shirts, dresses - you'll see it all. We care about you, not your outfit." },
    { icon: Music, title: "Worship That's Real", text: "Heartfelt, energetic worship that ministers to every age and walk of life." },
    { icon: BookOpen, title: "Preaching That Makes Sense", text: "Clear, Bible-centered teaching that connects to real life - no theology degree required." },
    { icon: Baby, title: "Kids Are Covered", text: "Safe, clean, fun VC Kids environments with a background-checked team and secure check-in." },
  ];
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="what-to-expect-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <h2 className="text-balance text-center font-display text-3xl font-900 tracking-tighter text-navy sm:text-5xl">
            WHAT TO EXPECT.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-base leading-relaxed text-slate-600">
            A service at Victory Chapel lasts about 60 minutes. Here's what walking in actually feels like.
          </p>
        </Reveal>
        <Stagger className="mt-10 grid gap-5 sm:mt-14 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {items.map((it) => (
            <StaggerItem key={it.title}>
              <div className="h-full rounded-3xl border border-navy/10 bg-cream p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white">
                  <it.icon size={22} aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-lg font-800 tracking-tight text-navy">{it.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.2}>
          <div className="mt-10 text-center">
            <Link to="/im-new" data-testid="wte-more-link"
                  className="inline-flex items-center gap-2 font-display text-sm font-700 text-victory transition-colors hover:text-victory-dark">
              EVERYTHING ELSE YOU'RE WONDERING <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ScriptureBand() {
  return (
    <section className="grain relative overflow-hidden bg-navy py-14 text-white sm:py-20" data-testid="scripture-section">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-victory/15 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="font-serif text-2xl italic leading-snug text-white/90 sm:text-3xl" data-testid="scripture-verse">
            "See, I am doing a new thing! Now it springs up; do you not perceive it?
            I am making a way in the wilderness and streams in the wasteland."
          </p>
          <p className="mt-5 font-display text-xs font-700 uppercase tracking-[0.35em] text-rose-300" data-testid="scripture-ref">
            Isaiah 43:19
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function MissionStrip() {
  return (
    <section className="bg-cream pb-14 pt-8 sm:pb-20 sm:pt-10" data-testid="mission-strip">
      <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-victory">Our Mission</p>
          <h2 className="mt-4 text-balance font-display text-3xl font-black leading-tight tracking-tight text-navy sm:text-5xl">
            <span className="text-victory">KNOW</span> Jesus. <span className="text-victory">GROW</span> in Faith. <span className="text-victory">GO</span> Share It.
          </h2>
        </Reveal>
      </div>
    </section>
  );
}

function ConnectStrip() {
  return (
    <section className="grain relative overflow-hidden bg-navy py-14 text-white sm:py-20" data-testid="connect-strip">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-victory/15 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="text-balance font-display text-3xl font-black tracking-tighter sm:text-5xl">
            HOW CAN WE PRAY FOR YOU?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            You don't have to wait for launch - and you don't have to attend - to ask for prayer.
            We're already here, and we'd be honored to pray with you.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3.5">
            <Link to="/prayer" data-testid="connect-prayer-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
              REQUEST PRAYER <ArrowRight size={15} aria-hidden="true" />
            </Link>
            <Link to="/contact" data-testid="connect-talk-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-700 text-white transition-colors duration-300 hover:bg-white/10">
              TALK WITH SOMEONE
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const launch = settings?.launch_mode !== false;
  const s = settings || {};

  return (
    <>
      <SEO
        title={launch ? "New Church in Middleburg, FL - Launching Jan 2027" : "You Belong Here - A Church for Clay County"}
        description="Victory Chapel Clay is a new church plant in Middleburg, FL - launching January 2027. Sundays 11 AM, Wednesdays 7 PM. Plan your first visit today."
        path="/"
        jsonLd={churchJsonLd}
      />
      {launch ? <PreLaunchHero settings={settings} /> : <PostLaunchHero settings={settings} />}
      <section className="bg-cream pb-0 pt-10 text-center sm:pt-14" data-testid="hero-support">
        <Reveal>
          <h2 className="mx-auto max-w-3xl px-5 font-display text-xl font-800 tracking-tight text-navy sm:text-3xl">
            Victory Chapel Clay &mdash; A New Church in Middleburg, Florida
          </h2>
        </Reveal>
      </section>
      <MissionStrip />
      <LocationMarquee />
      <WhatToExpect />
      <ScriptureBand />

      {launch && (
        <section id="service-1" className="bg-white py-16 sm:py-24" data-testid="service1-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Chapter number="00" eyebrow="Service #1" title="BE THERE FROM THE BEGINNING." />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Victory Chapel Clay is a church plant launching in Clay County - and we'd love for you and
                your family to be part of our very first service. Let us know you're coming so we can be
                ready to welcome you and your family.
              </p>
              <p className="mt-5 max-w-2xl font-serif text-lg italic text-navy/90 sm:text-xl">
                This isn't a visit to something finished - it's an invitation to help begin it.
              </p>
            </Reveal>
            <div className="mt-10 grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
              <Reveal delay={0.1}>
                <div className="overflow-hidden rounded-3xl shadow-lift">
                  <img
                    src="/launch-banner.jpg"
                    alt="Victory Chapel Clay - Know, Grow, Go. A new church launching January 2027 in Clay County, Florida"
                    className="w-full"
                    width="1942"
                    height="722"
                    loading="lazy"
                    data-testid="grand-opening-poster"
                  />
                </div>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    { icon: CalendarDays, top: "JANUARY 2027", sub: "Service #1" },
                    { icon: Heart, top: "EVERYONE WELCOME", sub: "VC Kids available" },
                    { icon: MapPin, top: "CLAY COUNTY, FL", sub: "3180 CR 220 STE 6-7, Middleburg" },
                  ].map((f) => (
                    <div key={f.top} className="rounded-2xl border border-navy/10 bg-white px-4 py-5 text-center shadow-soft" data-testid={`service1-feature-${f.top.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <f.icon size={20} className="mx-auto text-victory" aria-hidden="true" />
                      <p className="mt-2.5 font-display text-sm font-800 tracking-wide text-navy">{f.top}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{f.sub}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <Service1Form />
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <ConnectStrip />
    </>
  );
}
